import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Json } from '@/types/database.generated';
import type { HeldDraftInterval } from '@/lib/treatmentCourseWizard';
import { treatmentCourseStepSlotsQueryKey } from '@/lib/treatmentCourseSlotSearchKey';

export { treatmentCourseStepSlotsQueryKey } from '@/lib/treatmentCourseSlotSearchKey';

export type TreatmentCourseStepSlot = {
  starts_at: string;
  ends_at: string;
  duration_minutes: number;
  practitioner_ids: string[];
  resource_ids: string[];
};

export type FindTreatmentCourseStepSlotsInput = {
  clinicId: string;
  templateStepId?: string | null;
  bookedStepId?: string | null;
  windowStart?: string | null;
  windowEnd?: string | null;
  previousStartsAt?: string | null;
  practitionerIds?: string[] | null;
  held?: HeldDraftInterval[];
};

export function useEligiblePractitionersForCourseStep(
  bookedStepId: string | null | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ['eligible-practitioners-course-step', bookedStepId ?? null] as const,
    enabled: Boolean(enabled && bookedStepId),
    queryFn: async (): Promise<string[] | null> => {
      if (!bookedStepId) return [];
      const { data, error } = await supabase.rpc('eligible_practitioner_ids_for_course_step', {
        p_booked_step_id: bookedStepId,
      });
      if (error) throw error;
      return data;
    },
  });
}

export function useEligiblePractitionersForTemplateStep(
  stepId: string | null | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ['eligible-practitioners-template-step', stepId ?? null] as const,
    enabled: Boolean(enabled && stepId),
    queryFn: async (): Promise<string[] | null> => {
      if (!stepId) return [];
      const { data, error } = await supabase.rpc('eligible_practitioner_ids_for_template_step', {
        p_step_id: stepId,
      });
      if (error) throw error;
      return data;
    },
  });
}

export function useTreatmentCourseStepSlots(
  input: FindTreatmentCourseStepSlotsInput | null,
  enabled: boolean,
) {
  return useQuery({
    queryKey: treatmentCourseStepSlotsQueryKey(input),
    enabled: Boolean(enabled && input?.clinicId && (input.templateStepId || input.bookedStepId)),
    queryFn: async (): Promise<TreatmentCourseStepSlot[]> => {
      if (!input) return [];
      const { data, error } = await supabase.rpc('find_treatment_course_step_slots', {
        p_clinic_id: input.clinicId,
        p_template_step_id: input.templateStepId ?? undefined,
        p_booked_step_id: input.bookedStepId ?? undefined,
        p_window_start: input.windowStart ?? undefined,
        p_window_end: input.windowEnd ?? undefined,
        p_previous_starts_at: input.previousStartsAt ?? undefined,
        p_practitioner_ids:
          input.practitionerIds && input.practitionerIds.length > 0
            ? input.practitionerIds
            : undefined,
        p_held: (input.held ?? []) as unknown as Json,
      });
      if (error) throw error;
      return (data ?? []) as TreatmentCourseStepSlot[];
    },
  });
}

export function useBookTreatmentCourse(clinicId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      patientId: string;
      templateId: string;
      steps: Array<{
        template_step_id: string;
        starts_at: string | null;
        ends_at: string | null;
        practitioner_ids: string[];
        resource_ids: string[];
      }>;
    }) => {
      if (!clinicId) throw new Error('clinic_id is required');
      const { data, error } = await supabase.rpc('book_treatment_course', {
        p_clinic_id: clinicId,
        p_patient_id: input.patientId,
        p_template_id: input.templateId,
        p_steps: input.steps as unknown as Json,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['appointments'] });
      void queryClient.invalidateQueries({ queryKey: ['occupying-resource-allocations'] });
      void queryClient.invalidateQueries({ queryKey: ['appointment-resource-allocations'] });
      void queryClient.invalidateQueries({ queryKey: ['treatment-course-templates', clinicId ?? null] });
      void queryClient.invalidateQueries({ queryKey: ['treatment-courses'] });
    },
  });
}
